import { FormEvent, KeyboardEvent, useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  CircularProgress,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BoltIcon from "@mui/icons-material/Bolt";
import PersonIcon from "@mui/icons-material/Person";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BuildIcon from "@mui/icons-material/Build";
import EmailIcon from "@mui/icons-material/Email";

// Logic
import {
  FormData,
  calculateReport,
  typePresets,
} from "../generate-reports/calculateReport";
import { useAppDispatch } from "../../stores";
import { createEmbeddedEstimator } from "../../features/estimator-module/estimatorAction";
import { ViewGeneratedReportState } from "./EmbedViewGeneratedReport";

// --- STYLED COMPONENTS ---
const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(-1),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: "#bdbdbd" },
    "&.Mui-focused fieldset": { borderColor: "#3f51b5" },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: { fontSize: "0.8rem" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3f51b5" },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "28px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: { fontSize: "20px" },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: 22,
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    marginBottom: theme.spacing(1),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: { fontSize: "0.8rem" },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #455354)",
  color: "#fff",
  borderRadius: "8px",
  padding: theme.spacing(1, 5),
  fontSize: "16px",
  "&:hover": { backgroundColor: "#354142" },
  "&:disabled": { backgroundColor: "#bdbdbd", color: "#fff" },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
    minHeight: "48px",
  },
}));

const FreeChargingText = styled(Typography)(({ theme }) => ({
  color: "#2e7d32",
  fontWeight: "bold",
  fontSize: "0.975rem",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: { fontSize: "0.75rem" },
}));

interface EmbedGenerateReportProps {
  onSubmit: (state: ViewGeneratedReportState) => void;
  clientId: string;
}

const EmbedGenerateReport: React.FC<EmbedGenerateReportProps> = ({
  onSubmit,
  clientId,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Initial State matching the new requirements + Embed requirements
  const initialFormData: FormData = {
    address: "",
    city: "",
    zipCode: "",
    dwell: "60",
    dwellCategory: "Shopping Centers / Retail",
    chargers: "4",
    evs: "25", // Keeping numbers for embed, main app uses 'medium' but convert as needed
    type: "6.7-7.7-650-1500",
    hardware: "2600",
    install: "6000",
    pricingType: "kwh",
    pricePerKwh: "0.18", // Defaulting to 0.18 as we don't have API
    pricePerMin: "5",
    pricePerHour: "2",
    flatSessionFee: "2",
    idleFee: "5",
    gracePeriod: "10",
    maintenance: "100",
    networkPerPort: "0",
    grant: "1",
    firstName: "",
    lastName: "",
    email: "",
    clientId,

    // New Fields from Main App
    disableUtilityEnergyInfo: false,
    
    // Tax benefits defaults
    applyTaxBenefits: false,
    businessTaxRate: "21",
    section179Limit: "2500000",
    bonusDepreciationPct: "0",

    // Profit sharing defaults
    customerSplit: "100",
    softwareProviderSplit: "0",
    operatorSplit: "0",
  };

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const utilityRate = 0.18; // Fixed rate for embed since no API calls

  const chargingOutput = useMemo(() => {
    return Object.fromEntries(
      Object.keys(typePresets).map((key) => {
        const [min, max] = key.split("-").slice(0, 2);
        return [key, `${min}–${max} kW`];
      })
    );
  }, []);

  const chargingOutputOrder = useMemo(() => {
    return Object.keys(typePresets).sort((a, b) => {
      const wa = typePresets[a].whpm;
      const wb = typePresets[b].whpm;
      return wa - wb;
    });
  }, []);

  // Update hardware/install defaults when type changes
  useEffect(() => {
    const chargersNum = parseFloat(formData.chargers) || 1;
    const preset = typePresets[formData.type];
    if (preset) {
      setFormData((prev) => ({
        ...prev,
        hardware: (preset.hardware * chargersNum).toFixed(0),
        install: (preset.install * chargersNum).toFixed(0),
      }));
    }
  }, [formData.type, formData.chargers]);

  // Google Maps Logic (Same as before)
  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM&libraries=places`;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      const addressInput = document.getElementById(
        "address-input"
      ) as HTMLInputElement;
      if (addressInput) {
        const addressAutocomplete = new (window as any).google.maps.places.Autocomplete(
          addressInput,
          {
            types: ["address"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "geometry", "formatted_address"],
          }
        );

        addressAutocomplete.addListener("place_changed", () => {
          const place = addressAutocomplete.getPlace();
          const components = place.address_components || [];
          let city = "";
          let zipCode = "";

          components.forEach((cmp: any) => {
            if (cmp.types.includes("locality")) city = cmp.long_name;
            if (cmp.types.includes("postal_code")) zipCode = cmp.long_name;
          });

          setFormData((prev) => ({
            ...prev,
            address: place.formatted_address || prev.address,
            city: city || prev.city,
            zipCode: zipCode || prev.zipCode,
          }));

          setTouched((prev) => ({
            ...prev,
            address: true,
            city: true,
            zipCode: true,
          }));
        });
      }

      const zipInput = document.getElementById(
        "zipcode-input"
      ) as HTMLInputElement;
      if (zipInput) {
        const zipAutocomplete = new (window as any).google.maps.places.Autocomplete(
          zipInput,
          {
            types: ["(regions)"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "geometry", "formatted_address"],
          }
        );

        zipAutocomplete.addListener("place_changed", () => {
          const place = zipAutocomplete.getPlace();
          const components = place.address_components || [];
          let city = "";
          let address = "";
          let zipCode = "";

          components.forEach((cmp: any) => {
            if (cmp.types.includes("locality")) city = cmp.long_name;
            if (cmp.types.includes("postal_code")) zipCode = cmp.long_name;
          });

          address = place.formatted_address || city;

          setFormData((prev) => ({
            ...prev,
            address: address || prev.address,
            city: city || prev.city,
            zipCode: zipCode || prev.zipCode,
          }));

          setTouched((prev) => ({
            ...prev,
            address: true,
            city: true,
            zipCode: true,
          }));
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    const type = e.target.type;

    // Handle Checkboxes
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === "zipCode") {
      if (!/^\d*$/.test(value)) {
        toast.error("Zip Code can only contain numbers (0-9)");
        return;
      }
      if (value.length > 5) {
        toast.error("Zip Code must be exactly 5 digits");
        return;
      }
      if (value.length <= 5) {
        setErrors((prev) => ({ ...prev, zipCode: "" }));
      }
    }

    if (e.target.type === "number" && parseFloat(value) < 0) {
      toast.error(
        `${e.target.labels?.[0]?.textContent || "Field"} cannot be negative`
      );
      return;
    }

    // Name Validation
    if (["firstName", "lastName", "email"].includes(name)) {
      const message = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: message }));
    }

    // Handle select element extra data (dwell category)
    if (name === "dwell" && e.target instanceof HTMLSelectElement) {
      const select = e.target as HTMLSelectElement;
      const selectedOption = select.selectedOptions[0];
      const category = selectedOption
        ? selectedOption.dataset.category || ""
        : "";
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        dwellCategory: category,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (
      name === "address" ||
      name === "city" ||
      (name === "zipCode" && /^\d+$/.test(value))
    ) {
      const message = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: message }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const message = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: message }));
  };

  const validateField = (name: string, value: string) => {
    // Basic Required Fields
    if (
      ["address", "city", "zipCode", "firstName", "lastName", "email"].includes(
        name
      ) &&
      !value.trim()
    ) {
      return "This field is required";
    }
    if (name === "zipCode") {
      if (value && !/^\d+$/.test(value)) {
        return "Zip Code can only contain numbers (0-9)";
      }
      if (value && value.length !== 5) {
        return "Zip Code must be exactly 5 digits";
      }
    }
    if (name === "firstName" || name === "lastName") {
      if (value.length < 2) {
        return "Name must be at least 2 characters";
      }
      if (!/^[a-zA-Z\s]+$/.test(value)) {
        return "Name can only contain letters and spaces";
      }
    }
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Invalid email format";
      }
    }

    // Profit Sharing Validation
    if (
        name === "customerSplit" ||
        name === "softwareProviderSplit" ||
        name === "operatorSplit"
      ) {
        if (value && !/^\d*\.?\d*$/.test(value)) {
          return "Must be a valid number";
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          return "Must be between 0 and 100";
        }
  
        // Check total sum logic for immediate feedback (optional, handled in validateAll mostly)
        const customer = parseFloat(formData.customerSplit || "0") || 0;
        const software = parseFloat(formData.softwareProviderSplit || "0") || 0;
        const operator = parseFloat(formData.operatorSplit || "0") || 0;
        const currentFieldValue = numValue;
        
        // Simple heuristic: subtract old value and add new (simplified for blur validation)
        let total = 0;
        if(name === "customerSplit") total = currentFieldValue + software + operator;
        else if(name === "softwareProviderSplit") total = customer + currentFieldValue + operator;
        else if(name === "operatorSplit") total = customer + software + currentFieldValue;
  
        if (total > 100) {
          return `Total cannot exceed 100% (currently ${total.toFixed(1)}%)`;
        }
      }

    return "";
  };

  const validateAll = () => {
    const newErrors: Record<string, string> = {
      address: validateField("address", formData.address ?? ""),
      city: validateField("city", formData.city ?? ""),
      zipCode: validateField("zipCode", formData.zipCode ?? ""),
      firstName: validateField("firstName", formData.firstName ?? ""),
      lastName: validateField("lastName", formData.lastName ?? ""),
      email: validateField("email", formData.email ?? ""),
    };

    // Profit Sharing Sum Check
    const customer = parseFloat(formData.customerSplit || "0") || 0;
    const software = parseFloat(formData.softwareProviderSplit || "0") || 0;
    const operator = parseFloat(formData.operatorSplit || "0") || 0;
    const total = customer + software + operator;

    if (total !== 100) {
        const errorMsg = `Profit sharing must total exactly 100% (currently ${total.toFixed(1)}%)`;
        if(!newErrors.customerSplit) newErrors.customerSplit = errorMsg;
    }

    Object.keys(newErrors).forEach((k) => {
      if (!newErrors[k]) delete newErrors[k];
    });
    setErrors((prev) => ({ ...prev, ...newErrors }));
    setTouched((prev) => ({
      ...prev,
      address: true,
      city: true,
      zipCode: true,
      firstName: true,
      lastName: true,
      email: true,
      customerSplit: true,
      softwareProviderSplit: true,
      operatorSplit: true
    }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isValid = validateAll();
    if (!isValid) {
      toast.error("Please fill in the required fields correctly.");
      return;
    }
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      
      // Use the logic from main app, but we don't have APIs so utilityRate is fixed or from input
      const updatedReport = calculateReport(formData, 0.15, utilityRate);

      // Cash Flow Calculation (Simplified from main app)
      const upfrontCost = updatedReport.applyTaxBenefits
        ? updatedReport.upfrontAfterTax || 0
        : updatedReport.install + updatedReport.hardware + updatedReport.network;

      const cashFlowData = [-upfrontCost];
      for (let i = 0; i < 5; i++) {
        const yVal =
          updatedReport.chargingRevArr[i] +
          updatedReport.indirectArr[i] -
          updatedReport.maintenance / 5 -
          updatedReport.kWhYearArr[i] * utilityRate;
        cashFlowData.push(cashFlowData[i] + yVal);
      }

      // Profit Sharing Calculations
      const customerSplit = parseFloat(formData.customerSplit || "100") || 100;
      const softwareProviderSplit = parseFloat(formData.softwareProviderSplit || "0") || 0;
      const operatorSplit = parseFloat(formData.operatorSplit || "0") || 0;
      const netProfitBeforeSharing = updatedReport.netProfit5;
      
      const customerProfit = Math.round((customerSplit / 100) * netProfitBeforeSharing);
      const softwareProviderProfit = Math.round((softwareProviderSplit / 100) * netProfitBeforeSharing);
      const operatorProfit = Math.round((operatorSplit / 100) * netProfitBeforeSharing);

      const reportToSave = {
        firstName: formData.firstName || "N/A",
        lastName: formData.lastName || "N/A",
        email: formData.email || "N/A",
        clientId: formData.clientId || "N/A",
        reportName: `${formData.address} Charging Station`,
        location: formData.address || "N/A",
        netProfit: updatedReport.netProfit5,
        financialTable: [
          { category: "Charger Revenue (5yrs)", value: updatedReport.chargingRev5 },
          { category: "Added Dwell Time (per visit)", value: updatedReport.addedDwell },
          { category: "Additional Spend (per visit)", value: updatedReport.addSpend },
          { category: "Traffic Increase", value: updatedReport.traffic },
          { category: "Indirect Revenue (5yrs)", value: updatedReport.indirectRev5 },
          { category: "Total Revenue", value: updatedReport.totalRev5 },
          { category: "Install Cost", value: updatedReport.install },
          { category: "Hardware Cost", value: updatedReport.hardware },
          { category: "Network Cost", value: updatedReport.network },
          { category: "Maintenance", value: updatedReport.maintenance },
          { category: "Utility Cost", value: updatedReport.utilityCost5 },
          { category: "Grants/Subsidies & Tax Credit", value: updatedReport.grant },
          ...(updatedReport.applyTaxBenefits
            ? [
                {
                  category: "Section 179 / 168(k) Tax Savings (Est.)",
                  value: Math.round(updatedReport.taxSavings || 0),
                },
              ]
            : []),
          { category: `Customer Share (${customerSplit}%)`, value: customerProfit },
          { category: `Software Provider Share (${softwareProviderSplit}%)`, value: softwareProviderProfit },
          { category: `Operator Share (${operatorSplit}%)`, value: operatorProfit },
          { category: "Net Profit", value: updatedReport.netProfit5 },
        ],
        breakEvenCashFlow: {
          labels: ["0", "1", "2", "3", "4", "5"],
          data: cashFlowData,
        },
        revenueCost: {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [
            { label: "Charging Rev", data: updatedReport.chargingRevArr },
            { label: "Indirect Rev", data: updatedReport.indirectArr },
            { label: "Install Cost", data: [updatedReport.install, 0, 0, 0, 0] },
            { label: "Hardware Cost", data: [updatedReport.hardware, 0, 0, 0, 0] },
            { label: "Network Cost", data: [updatedReport.network, 0, 0, 0, 0] },
            { label: "Maintenance", data: Array(5).fill(updatedReport.maintenance / 5) },
            { label: "Utility Cost", data: updatedReport.kWhYearArr.map((k) => k * utilityRate) },
          ],
        },
        co2Savings: {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [
            { label: "CO₂ Offset (kg)", data: updatedReport.co2Arr.map((x) => Math.round(x)) },
            { label: "Gasoline Replaced (gal)", data: updatedReport.gasArr.map((x) => Math.round(x)) },
          ],
        },
        utilization: 15,
        utilityRate,
        timestamp: new Date().toISOString(),
        
        // Save new fields data
        applyTaxBenefits: updatedReport.applyTaxBenefits,
        businessTaxRate: updatedReport.businessTaxRate,
        section179Limit: updatedReport.section179Limit,
        bonusDepreciationPct: updatedReport.bonusDepreciationPct,
        customerSplit,
        softwareProviderSplit,
        operatorSplit,
        disableUtilityEnergyInfo: formData.disableUtilityEnergyInfo
      };

      await dispatch(createEmbeddedEstimator(reportToSave)).unwrap();
      
      // Passing state (though we show thank you screen immediately)
      const stateToPass: ViewGeneratedReportState = {
        formData,
        reportData: updatedReport,
        utilization: 15,
        utilityRate,
      };
      
      setFormSubmitted(true);

    } catch (error) {
      console.error("Error generating or saving report.");
      toast.error("Failed to generate report.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  const renderPricingInputs = () => {
    switch (formData.pricingType) {
      case "kwh":
        return (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldLabel>Price per kWh (¢/kWh)</FieldLabel>
            <StyledTextField
              name="pricePerKwh"
              variant="outlined"
              type="number"
              value={formData.pricePerKwh}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "minute":
        return (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldLabel>Price per minute (¢/min)</FieldLabel>
            <StyledTextField
              name="pricePerMin"
              variant="outlined"
              type="number"
              value={formData.pricePerMin}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "hour":
        return (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldLabel>Price per hour ($/hr)</FieldLabel>
            <StyledTextField
              name="pricePerHour"
              variant="outlined"
              type="number"
              value={formData.pricePerHour}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "session":
        return (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FieldLabel>Flat session fee ($)</FieldLabel>
            <StyledTextField
              name="flatSessionFee"
              variant="outlined"
              type="number"
              value={formData.flatSessionFee}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "minute_idle":
        return (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Price per minute (¢/min)</FieldLabel>
              <StyledTextField
                name="pricePerMin"
                variant="outlined"
                type="number"
                value={formData.pricePerMin}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Idle/Overstay Fee ($)</FieldLabel>
              <StyledTextField
                name="idleFee"
                variant="outlined"
                type="number"
                value={formData.idleFee}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Grace Period (minutes)</FieldLabel>
              <StyledTextField
                name="gracePeriod"
                variant="outlined"
                type="number"
                value={formData.gracePeriod}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        );
      case "kwh_idle":
        return (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Price per kWh (¢/kWh)</FieldLabel>
              <StyledTextField
                name="pricePerKwh"
                variant="outlined"
                type="number"
                value={formData.pricePerKwh}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Idle/Overstay Fee ($)</FieldLabel>
              <StyledTextField
                name="idleFee"
                variant="outlined"
                type="number"
                value={formData.idleFee}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Grace Period (minutes)</FieldLabel>
              <StyledTextField
                name="gracePeriod"
                variant="outlined"
                type="number"
                value={formData.gracePeriod}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        );
      case "free":
        return (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FreeChargingText>
              Charging is free for the driver.
            </FreeChargingText>
          </Grid>
        );
      default:
        return null;
    }
  };

  if (formSubmitted) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          background: "#fff",
        }}
      >
        <Typography variant="h3" sx={{ color: "#455354", fontWeight: 700 }}>
          Thank You
        </Typography>
        <Typography sx={{ mt: 2, fontSize: "1.2rem", color: "#555" }}>
          We have received your details. Your report is being prepared and will be emailed to you shortly.
        </Typography>
      </Box>
    );
  }

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyPress}
          >
            <WelcomeText>
              Generate Report - L2 Charging Station Estimator
            </WelcomeText>
            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                padding: 2.5,
                mt: 2.5,
                borderRadius: 2,
              }}
            >
              <SubHeading>User Information</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>First Name*</FieldLabel>
                  <StyledTextField
                    name="firstName"
                    variant="outlined"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={
                      touched.firstName && errors.firstName
                        ? errors.firstName
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Last Name*</FieldLabel>
                  <StyledTextField
                    name="lastName"
                    variant="outlined"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={
                      touched.lastName && errors.lastName ? errors.lastName : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Email*</FieldLabel>
                  <StyledTextField
                    name="email"
                    variant="outlined"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.email && errors.email)}
                    helperText={
                      touched.email && errors.email ? errors.email : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <SubHeading>Location Details</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Address*</FieldLabel>
                  <StyledTextField
                    id="address-input"
                    name="address"
                    variant="outlined"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.address && errors.address)}
                    helperText={
                      touched.address && errors.address ? errors.address : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>City*</FieldLabel>
                  <StyledTextField
                    name="city"
                    variant="outlined"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city ? errors.city : ""}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Zip Code*</FieldLabel>
                  <StyledTextField
                    id="zipcode-input"
                    name="zipCode"
                    variant="outlined"
                    type="text"
                    value={formData.zipCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.zipCode && errors.zipCode)}
                    helperText={
                      touched.zipCode && errors.zipCode
                        ? errors.zipCode
                        : `Utility Rate: ${utilityRate.toFixed(4)} $/kWh`
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{
                      maxLength: 5,
                      inputMode: "numeric",
                      pattern: "[0-9]{5}",
                      placeholder: "12345",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <SubHeading>Charging Station Details</SubHeading>
              {/* Demand Charge Exclusion */}
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        id="disableUtilityEnergyInfo"
                        name="disableUtilityEnergyInfo"
                        checked={formData.disableUtilityEnergyInfo}
                        onChange={handleChange}
                        sx={{
                            color: "#4b5b5d",
                            '&.Mui-checked': {
                                color: "#455354",
                            },
                        }}
                      />
                      <label
                        htmlFor="disableUtilityEnergyInfo"
                        style={{ fontSize: "0.9rem", color: "#455354", fontWeight: 500 }}
                      >
                        Exclude Demand Charges
                      </label>
                    </Box>
                  </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Average Parking Time*</FieldLabel>
                  <StyledTextField
                    select
                    name="dwell"
                    variant="outlined"
                    value={formData.dwell}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem
                      value="60"
                      data-category="Shopping Centers / Retail"
                    >
                      Shopping Centers / Retail (60m)
                    </MenuItem>
                    <MenuItem
                      value="0"
                      data-category="Apartment Complex / Multi-family"
                    >
                      Apartment Complex / Multi-family (14-18h)
                    </MenuItem>
                    <MenuItem value="600" data-category="Hotels / Lodging">
                      Hotels / Lodging (8-12h)
                    </MenuItem>
                    <MenuItem value="480" data-category="Office Buildings">
                      Office Buildings (8h)
                    </MenuItem>
                    <MenuItem value="90" data-category="Restaurants / Cafés">
                      Restaurants / Cafés (1-2h)
                    </MenuItem>
                    <MenuItem
                      value="150"
                      data-category="Theaters / Entertainment"
                    >
                      Theaters / Entertainment (2-3h)
                    </MenuItem>
                    <MenuItem value="90" data-category="Gyms / Fitness">
                      Gyms / Fitness (1-2h)
                    </MenuItem>
                    <MenuItem value="00" data-category="Hospitals / Medical">
                      Hospitals / Medical (2-8h)
                    </MenuItem>
                    <MenuItem value="000" data-category="Universities / Campus">
                      Universities / Campus (4-8h)
                    </MenuItem>
                    <MenuItem
                      value="1380"
                      data-category="Parking Garages / Lots"
                    >
                      Parking Garages / Lots (23h)
                    </MenuItem>
                  </StyledTextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Chargers*</FieldLabel>
                  <StyledTextField
                    name="chargers"
                    variant="outlined"
                    type="number"
                    value={formData.chargers}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BoltIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>EV Drivers*</FieldLabel>
                  <StyledTextField
                    name="evs"
                    variant="outlined"
                    type="number"
                    value={formData.evs}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Charging Output*</FieldLabel>
                  <StyledTextField
                    select
                    name="type"
                    variant="outlined"
                    value={formData.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BatteryChargingFullIcon />
                        </InputAdornment>
                      ),
                    }}
                    SelectProps={{
                      renderValue: (value) => chargingOutput[value as string],
                    }}
                  >
                    {chargingOutputOrder.map((key) => (
                      <MenuItem key={key} value={key}>
                        {chargingOutput[key]}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Hardware Cost (Total)*</FieldLabel>
                  <StyledTextField
                    name="hardware"
                    variant="outlined"
                    type="number"
                    value={formData.hardware}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Install Cost (Total)*</FieldLabel>
                  <StyledTextField
                    name="install"
                    variant="outlined"
                    type="number"
                    value={formData.install}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Driver Pricing</FieldLabel>
                  <StyledTextField
                    select
                    name="pricingType"
                    variant="outlined"
                    value={formData.pricingType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="kwh">Per kWh</MenuItem>
                    <MenuItem value="minute">Per Minute</MenuItem>
                    <MenuItem value="hour">Per Hour</MenuItem>
                    <MenuItem value="session">Flat Session Fee</MenuItem>
                    <MenuItem value="minute_idle">
                      Per Minute + Idle/Overstay Fee
                    </MenuItem>
                    <MenuItem value="kwh_idle">
                      Per kWh + Idle/Overstay Fee
                    </MenuItem>
                    <MenuItem value="free">Free Charging</MenuItem>
                  </StyledTextField>
                </Grid>
                {renderPricingInputs()}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Annual Maintenance (S/Charger)</FieldLabel>
                  <StyledTextField
                    name="maintenance"
                    variant="outlined"
                    type="number"
                    value={formData.maintenance}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BuildIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Network Cost Per Port</FieldLabel>
                  <StyledTextField
                    name="networkPerPort"
                    variant="outlined"
                    type="number"
                    value={formData.networkPerPort}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Additional Grant/Subsidy</FieldLabel>
                  <StyledTextField
                    name="grant"
                    variant="outlined"
                    type="number"
                    value={formData.grant}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* --- New Section: Tax Benefits --- */}
              <SubHeading>Tax Benefits (Section 179 and 168(k))</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                  <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox
                            id="applyTaxBenefits"
                            name="applyTaxBenefits"
                            checked={formData.applyTaxBenefits}
                            onChange={handleChange}
                            sx={{
                                color: "#4b5b5d",
                                '&.Mui-checked': {
                                    color: "#455354",
                                },
                            }}
                        />
                        <FieldLabel>
                          Apply Section 179 and 168(k) Tax Benefits?
                        </FieldLabel>
                      </Box>
                      <Typography variant="caption" sx={{ color: "#666", ml: 3, display: "block" }}>
                        Uses 2025 United States limits for EV equipment deductions. Consult your CPA.
                      </Typography>
                  </Grid>

                  {formData.applyTaxBenefits && (
                      <>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FieldLabel>Business Tax Rate (%)*</FieldLabel>
                          <StyledTextField
                            name="businessTaxRate"
                            variant="outlined"
                            type="number"
                            value={formData.businessTaxRate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            size={isMobile ? "small" : "medium"}
                            inputProps={{ min: "0", max: "100", step: "0.1" }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FieldLabel>Section 179 Limit ($)*</FieldLabel>
                          <StyledTextField
                            name="section179Limit"
                            variant="outlined"
                            type="number"
                            value={formData.section179Limit}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            size={isMobile ? "small" : "medium"}
                            inputProps={{ min: "0", step: "1000" }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FieldLabel>Bonus Depreciation (168(k)) %*</FieldLabel>
                          <StyledTextField
                            name="bonusDepreciationPct"
                            variant="outlined"
                            type="number"
                            value={formData.bonusDepreciationPct}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            size={isMobile ? "small" : "medium"}
                            inputProps={{ min: "0", max: "100", step: "1" }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                             <Box sx={{ mt: 1, p: 1, backgroundColor: "#fff3cd", borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: "#856404", fontStyle: "normal" }}>
                                  <strong>Tax disclaimer:</strong> Estimates are illustrative only and do not constitute tax advice.
                                </Typography>
                             </Box>
                        </Grid>
                      </>
                  )}
              </Grid>

              {/* --- New Section: Profit Sharing --- */}
              <SubHeading>Profit Sharing</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Customer Split (%)*</FieldLabel>
                  <StyledTextField
                    name="customerSplit"
                    variant="outlined"
                    type="number"
                    value={formData.customerSplit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }}
                    error={Boolean(errors.customerSplit)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Software Provider Split (%)*</FieldLabel>
                  <StyledTextField
                    name="softwareProviderSplit"
                    variant="outlined"
                    type="number"
                    value={formData.softwareProviderSplit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Operator Split (%)*</FieldLabel>
                  <StyledTextField
                    name="operatorSplit"
                    variant="outlined"
                    type="number"
                    value={formData.operatorSplit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }}
                  />
                </Grid>
                {errors.customerSplit && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ color: "#d32f2f" }}>
                      {errors.customerSplit}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 2,
                }}
              >
                <motion.div
                  whileHover={{
                    scale: 1.04,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <PrimaryButton
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ? "Generating..." : "Calculate"}
                  </PrimaryButton>
                </motion.div>
              </Box>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default EmbedGenerateReport;